/**
 * Client Progress Page - Body Measurements & Progress Photos (Epic 4)
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Dumbbell,
  Ruler,
  Camera,
  Plus,
  Trash2,
  Loader2,
  Weight,
  TrendingDown,
  TrendingUp,
  ImageIcon,
} from "lucide-react";
import {
  useMyMeasurements,
  useMyPhotos,
  createMeasurement,
  deleteMeasurement,
  uploadProgressPhoto,
  deleteProgressPhoto,
} from "../hooks/useMeasurements";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster, toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const MEASUREMENT_FIELDS = [
  { key: "weight_kg", label: "Poids", unit: "kg", icon: Weight },
  { key: "body_fat_percent", label: "Masse grasse", unit: "%", icon: TrendingDown },
  { key: "chest_cm", label: "Poitrine", unit: "cm", icon: Ruler },
  { key: "waist_cm", label: "Taille", unit: "cm", icon: Ruler },
  { key: "hips_cm", label: "Hanches", unit: "cm", icon: Ruler },
  { key: "arm_cm", label: "Bras", unit: "cm", icon: Ruler },
  { key: "thigh_cm", label: "Cuisses", unit: "cm", icon: Ruler },
];

const PHOTO_TYPES = [
  { value: "front", label: "Face" },
  { value: "back", label: "Dos" },
  { value: "side", label: "Profil" },
];

export default function ProgressPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("charts");
  const { measurements, mutate: mutateMeasurements } = useMyMeasurements();
  const { photos, mutate: mutatePhotos } = useMyPhotos();

  const [measureDialogOpen, setMeasureDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [measureForm, setMeasureForm] = useState({
    measured_at: new Date().toISOString().split("T")[0],
    weight_kg: "",
    body_fat_percent: "",
    chest_cm: "",
    waist_cm: "",
    hips_cm: "",
    arm_cm: "",
    thigh_cm: "",
  });

  const [photoForm, setPhotoForm] = useState({
    file: null,
    photo_type: "front",
    taken_at: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const handleAddMeasurement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { measured_at: measureForm.measured_at };
      MEASUREMENT_FIELDS.forEach(({ key }) => {
        const val = measureForm[key];
        if (val !== "" && val !== null && val !== undefined) {
          payload[key] = parseFloat(val);
        }
      });
      await createMeasurement(payload);
      toast.success("Mesure ajoutée");
      setMeasureDialogOpen(false);
      setMeasureForm({
        measured_at: new Date().toISOString().split("T")[0],
        weight_kg: "", body_fat_percent: "", chest_cm: "",
        waist_cm: "", hips_cm: "", arm_cm: "", thigh_cm: "",
      });
      mutateMeasurements();
    } catch (err) {
      toast.error(err.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeasurement = async (id) => {
    try {
      await deleteMeasurement(id);
      toast.success("Mesure supprimée");
      mutateMeasurements();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    if (!photoForm.file) {
      toast.error("Sélectionnez une photo");
      return;
    }
    setSaving(true);
    try {
      await uploadProgressPhoto(
        photoForm.file,
        photoForm.photo_type,
        photoForm.taken_at,
        photoForm.notes || null
      );
      toast.success("Photo uploadée");
      setPhotoDialogOpen(false);
      setPhotoForm({ file: null, photo_type: "front", taken_at: new Date().toISOString().split("T")[0], notes: "" });
      mutatePhotos();
    } catch (err) {
      toast.error(err.message || "Erreur d'upload");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhoto = async (id) => {
    try {
      await deleteProgressPhoto(id);
      toast.success("Photo supprimée");
      mutatePhotos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Compute trends for latest vs previous measurement
  const latestMeasure = measurements[0];
  const prevMeasure = measurements[1];

  // Chart data (reversed to show chronological order)
  const chartData = [...measurements].reverse().map((m) => ({
    date: new Date(m.measured_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
    Poids: m.weight_kg,
    "Masse grasse": m.body_fat_percent,
  }));

  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
              className="text-zinc-400 hover:text-white -ml-2"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-lime-400" />
              <span className="font-heading font-bold text-white">MA PROGRESSION</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-zinc-900/50 p-1 rounded-full border border-zinc-800">
          <button
            onClick={() => setActiveTab("charts")}
            data-testid="tab-charts"
            className={`flex-1 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              activeTab === "charts"
                ? "bg-lime-400 text-zinc-950"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            Graphiques
          </button>
          <button
            onClick={() => setActiveTab("measurements")}
            data-testid="tab-measurements"
            className={`flex-1 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              activeTab === "measurements"
                ? "bg-lime-400 text-zinc-950"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            Mesures
          </button>
          <button
            onClick={() => setActiveTab("photos")}
            data-testid="tab-photos"
            className={`flex-1 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              activeTab === "photos"
                ? "bg-lime-400 text-zinc-950"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            Photos
          </button>
        </div>
      </div>

      <main className="max-w-lg mx-auto px-4 py-6">
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6" key={activeTab}>
          {activeTab === "charts" ? (
            <>
              {chartData.length >= 2 ? (
                <>
                  <motion.div variants={itemVariants}>
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base text-white">Poids (kg)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
                            <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fill: "#71717a", fontSize: 11 }} width={40} />
                            <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }} labelStyle={{ color: "#a1a1aa" }} />
                            <Line type="monotone" dataKey="Poids" stroke="#a3e635" strokeWidth={2.5} dot={{ fill: "#a3e635", r: 4 }} activeDot={{ r: 6 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base text-white">Masse grasse (%)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
                            <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fill: "#71717a", fontSize: 11 }} width={40} />
                            <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }} labelStyle={{ color: "#a1a1aa" }} />
                            <Line type="monotone" dataKey="Masse grasse" stroke="#38bdf8" strokeWidth={2.5} dot={{ fill: "#38bdf8", r: 4 }} activeDot={{ r: 6 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                </>
              ) : (
                <motion.div variants={itemVariants}>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-12 text-center">
                      <TrendingUp className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">Ajoutez au moins 2 mesures pour voir les graphiques</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </>
          ) : activeTab === "measurements" ? (
            <>
              {/* Add button */}
              <motion.div variants={itemVariants}>
                <Button
                  onClick={() => setMeasureDialogOpen(true)}
                  data-testid="add-measurement-button"
                  className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nouvelle mesure
                </Button>
              </motion.div>

              {/* Latest measurement summary */}
              {latestMeasure && (
                <motion.div variants={itemVariants}>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-base text-white">
                          Dernière mesure
                        </CardTitle>
                        <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/30 text-xs">
                          {new Date(latestMeasure.measured_at).toLocaleDateString("fr-FR")}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        {MEASUREMENT_FIELDS.map(({ key, label, unit }) => {
                          const val = latestMeasure[key];
                          if (val === null || val === undefined) return null;
                          const prevVal = prevMeasure?.[key];
                          const diff = prevVal != null ? val - prevVal : null;
                          return (
                            <div key={key} className="flex items-center justify-between p-2.5 bg-zinc-950/50 rounded-lg border border-zinc-800/50">
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
                                <p className="text-lg font-bold text-white">{val}<span className="text-xs text-zinc-500 ml-0.5">{unit}</span></p>
                              </div>
                              {diff !== null && diff !== 0 && (
                                <span className={`text-xs font-bold flex items-center gap-0.5 ${diff < 0 ? "text-emerald-400" : "text-red-400"}`}>
                                  {diff < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                  {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* History */}
              {measurements.length > 0 ? (
                <motion.div variants={itemVariants}>
                  <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Historique</h3>
                  <div className="space-y-2">
                    {measurements.map((m) => (
                      <Card key={m.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`measurement-${m.id}`}>
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white">
                              {new Date(m.measured_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                            </p>
                            <p className="text-xs text-zinc-500 truncate">
                              {[
                                m.weight_kg && `${m.weight_kg}kg`,
                                m.body_fat_percent && `${m.body_fat_percent}% BF`,
                                m.chest_cm && `Poit: ${m.chest_cm}`,
                                m.waist_cm && `Taille: ${m.waist_cm}`,
                              ].filter(Boolean).join(" | ")}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteMeasurement(m.id)}
                            data-testid={`delete-measurement-${m.id}`}
                            className="text-zinc-500 hover:text-red-400 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div variants={itemVariants}>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-12 text-center">
                      <Ruler className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">Aucune mesure enregistrée</p>
                      <p className="text-zinc-600 text-sm mt-1">Ajoutez votre première mesure pour suivre votre progression</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </>
          ) : (
            <>
              {/* Add photo button */}
              <motion.div variants={itemVariants}>
                <Button
                  onClick={() => setPhotoDialogOpen(true)}
                  data-testid="add-photo-button"
                  className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Nouvelle photo
                </Button>
              </motion.div>

              {/* Photos grid */}
              {photos.length > 0 ? (
                <motion.div variants={itemVariants}>
                  <div className="grid grid-cols-2 gap-3">
                    {photos.map((photo) => (
                      <Card key={photo.id} className="bg-zinc-900/50 border-zinc-800 overflow-hidden" data-testid={`photo-${photo.id}`}>
                        <div className="aspect-[3/4] relative group">
                          <img
                            src={photo.photo_url}
                            alt={`${photo.photo_type} - ${photo.taken_at}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeletePhoto(photo.id)}
                              data-testid={`delete-photo-${photo.id}`}
                              className="text-red-400 hover:bg-red-400/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <CardContent className="p-2.5">
                          <div className="flex items-center justify-between">
                            <Badge className="bg-zinc-800 text-zinc-300 text-xs capitalize">
                              {PHOTO_TYPES.find((t) => t.value === photo.photo_type)?.label || photo.photo_type}
                            </Badge>
                            <span className="text-xs text-zinc-500">
                              {new Date(photo.taken_at).toLocaleDateString("fr-FR")}
                            </span>
                          </div>
                          {photo.notes && <p className="text-xs text-zinc-500 mt-1 truncate">{photo.notes}</p>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div variants={itemVariants}>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-12 text-center">
                      <ImageIcon className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">Aucune photo d'évolution</p>
                      <p className="text-zinc-600 text-sm mt-1">Uploadez votre première photo pour suivre votre transformation</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </main>

      {/* Add Measurement Dialog */}
      <Dialog open={measureDialogOpen} onOpenChange={setMeasureDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Nouvelle mesure</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMeasurement} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Date</Label>
              <Input
                type="date"
                value={measureForm.measured_at}
                onChange={(e) => setMeasureForm({ ...measureForm, measured_at: e.target.value })}
                required
                data-testid="measure-date"
                className="bg-zinc-950 border-zinc-800 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {MEASUREMENT_FIELDS.map(({ key, label, unit }) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs">{label} ({unit})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={measureForm[key]}
                    onChange={(e) => setMeasureForm({ ...measureForm, [key]: e.target.value })}
                    placeholder="—"
                    data-testid={`measure-${key}`}
                    className="bg-zinc-950 border-zinc-800 h-10"
                  />
                </div>
              ))}
            </div>
            <Button
              type="submit"
              disabled={saving}
              data-testid="submit-measurement"
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Photo Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Nouvelle photo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadPhoto} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Photo</Label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoForm({ ...photoForm, file: e.target.files[0] })}
                  data-testid="photo-file-input"
                  className="w-full h-24 bg-zinc-950 border border-dashed border-zinc-700 rounded-lg file:hidden text-zinc-400 text-sm flex items-center justify-center cursor-pointer"
                />
                {photoForm.file && (
                  <p className="text-xs text-lime-400 mt-1">{photoForm.file.name}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Type</Label>
                <Select
                  value={photoForm.photo_type}
                  onValueChange={(v) => setPhotoForm({ ...photoForm, photo_type: v })}
                >
                  <SelectTrigger data-testid="photo-type-select" className="bg-zinc-950 border-zinc-800 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {PHOTO_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Date</Label>
                <Input
                  type="date"
                  value={photoForm.taken_at}
                  onChange={(e) => setPhotoForm({ ...photoForm, taken_at: e.target.value })}
                  required
                  data-testid="photo-date"
                  className="bg-zinc-950 border-zinc-800 h-11"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes (optionnel)</Label>
              <Input
                value={photoForm.notes}
                onChange={(e) => setPhotoForm({ ...photoForm, notes: e.target.value })}
                placeholder="Ex: Après 4 semaines de programme"
                data-testid="photo-notes"
                className="bg-zinc-950 border-zinc-800 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={saving || !photoForm.file}
              data-testid="submit-photo"
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Uploader"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
